import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from "antd";
import { CheckOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { errorText, listAccounts as listLedgerAccounts } from "../api";
import { formatCents, formatDate } from "../shared/money";
import MoneyInput from "../shared/MoneyInput";
import Amount from "../shared/Amount";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { MATCH_TYPE_LABELS, STATUS_COLORS, STATUS_LABELS } from "./constants";
import {
  getTransaction,
  ignoreTransaction,
  matchTransaction,
  searchTargets,
  unmatchTransaction
} from "./api";

const TAX_RATES = [
  { value: 19, label: "19 %" },
  { value: 7, label: "7 %" },
  { value: 0, label: "0 %" }
];

const rowFromSuggestion = s => ({
  type: s.type,
  ref: s.ref || null,
  account: s.account || "",
  category: s.category || "",
  taxRate: s.taxRate ?? null,
  amount: s.amount,
  skonto: s.skonto || 0,
  fee: 0,
  batch: s.batch || null,
  label: s.label || ""
});

const rowsFromSuggestion = s =>
  s.items && s.items.length
    ? s.items.map(i => ({ ...rowFromSuggestion(i), batch: s.batch || null }))
    : [rowFromSuggestion(s)];

/** Manual search of invoices / Belege / payroll runs / filings. */
const TargetSearch = ({ direction, onPick }) => {
  const { t } = useTranslation("accounting");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await searchTargets({ q, direction });
      setResults(res.data);
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Input.Search
        placeholder={t("Search by number, name or amount")}
        value={q}
        onChange={e => setQ(e.target.value)}
        onSearch={run}
        loading={busy}
        enterButton={<SearchOutlined />}
      />
      {results ? (
        <div style={{ maxHeight: 260, overflow: "auto", marginTop: 8 }}>
          {results.invoices.map(i => (
            <Card
              key={i._id}
              size="small"
              hoverable
              style={{ marginBottom: 6 }}
              onClick={() =>
                onPick({
                  type: "invoice",
                  ref: i._id,
                  amount: i.open,
                  label: `${i.invoiceNumber} ${i.customer}`
                })
              }
            >
              <Space wrap>
                <Tag>{t("Invoice")}</Tag>
                <Typography.Text strong>{i.invoiceNumber}</Typography.Text>
                <Typography.Text>{i.customer}</Typography.Text>
                <Typography.Text type="secondary">
                  {t("open")} <span className="acct-num">{formatCents(i.open)}</span>
                </Typography.Text>
              </Space>
            </Card>
          ))}
          {results.belege.map(b => (
            <Card
              key={b._id}
              size="small"
              hoverable
              style={{ marginBottom: 6 }}
              onClick={() =>
                onPick({
                  type: "beleg",
                  ref: b._id,
                  amount: b.open - (b.paid ? 0 : b.withheld),
                  label: `${b.invoiceNumber || b.belegNumber} ${b.supplierName}`
                })
              }
            >
              <Space wrap>
                <Tag>{t("Supplier invoice")}</Tag>
                <Typography.Text strong>{b.invoiceNumber || b.belegNumber}</Typography.Text>
                <Typography.Text>{b.supplierName}</Typography.Text>
                <Typography.Text type="secondary">
                  {t("open")} {formatCents(b.open)}
                  {b.withheld ? ` (− ${t("Bauabzugsteuer")} ${formatCents(b.withheld)})` : ""}
                </Typography.Text>
              </Space>
            </Card>
          ))}
          {results.payrollRuns.map(r => (
            <Card
              key={r._id}
              size="small"
              hoverable
              style={{ marginBottom: 6 }}
              onClick={() =>
                onPick({
                  type: "payroll_run",
                  ref: r._id,
                  amount: r.net,
                  label: `Lohn ${r.period}`
                })
              }
            >
              <Space wrap>
                <Tag>{t("Payroll run")}</Tag>
                <Typography.Text strong>{r.period}</Typography.Text>
                <Typography.Text type="secondary">
                  <span className="acct-num">{formatCents(r.net)}</span>
                </Typography.Text>
              </Space>
            </Card>
          ))}
          {results.filings.map(f => (
            <Card
              key={f._id}
              size="small"
              hoverable
              style={{ marginBottom: 6 }}
              onClick={() =>
                onPick({
                  type: "filing",
                  ref: f._id,
                  amount: f.amount,
                  label: `${f.type} ${f.period}`
                })
              }
            >
              <Space wrap>
                <Tag>{t("Tax filing")}</Tag>
                <Typography.Text strong>
                  {f.type} {f.period}
                </Typography.Text>
                <Typography.Text type="secondary">
                  <span className="acct-num">{formatCents(f.amount)}</span>
                </Typography.Text>
              </Space>
            </Card>
          ))}
          {!results.invoices.length &&
          !results.belege.length &&
          !results.payrollRuns.length &&
          !results.filings.length ? (
            <Typography.Text type="secondary">{t("No results")}</Typography.Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/** One row of the split editor. */
const MatchRow = ({ row, index, ledgerAccounts, categories, incoming, onChange, onRemove }) => {
  const { t } = useTranslation("accounting");
  const set = patch => onChange(index, { ...row, ...patch });
  const isDoc = ["invoice", "beleg", "payroll_run", "filing"].includes(row.type);
  return (
    <Card
      size="small"
      style={{ marginBottom: 8 }}
      extra={
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          aria-label={t("Delete")}
          onClick={() => onRemove(index)}
        />
      }
    >
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12} md={8}>
          <Select
            style={{ width: "100%" }}
            value={row.type}
            onChange={v =>
              set({
                type: v,
                ref: v === "transfer" ? row.ref : null,
                account: "",
                label: isDoc ? "" : row.label
              })
            }
            options={Object.entries(MATCH_TYPE_LABELS).map(([value, label]) => ({
              value,
              label: t(label)
            }))}
          />
        </Col>
        {isDoc ? (
          <Col xs={24} sm={12} md={16} className="acct-wrap" style={{ alignSelf: "center" }}>
            <Typography.Text strong>
              {row.label || row.ref || t("(pick a document below)")}
            </Typography.Text>
          </Col>
        ) : row.type === "account" ? (
          <>
            <Col xs={24} sm={12} md={8}>
              <Select
                showSearch
                allowClear
                style={{ width: "100%" }}
                placeholder={t("Category")}
                value={row.category || undefined}
                onChange={v => set({ category: v || "", account: "" })}
                optionFilterProp="label"
                options={(categories || []).map(c => ({ value: c.key, label: c.label }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                showSearch
                allowClear
                style={{ width: "100%" }}
                placeholder={t("or account")}
                value={row.account || undefined}
                onChange={v => set({ account: v || "", category: "" })}
                optionFilterProp="label"
                options={(ledgerAccounts || []).map(a => ({
                  value: a.number,
                  label: `${a.number} ${a.name}`
                }))}
              />
            </Col>
            {!incoming ? (
              <Col xs={24} sm={12} md={8}>
                <Select
                  style={{ width: "100%" }}
                  allowClear
                  placeholder={t("VAT")}
                  value={row.taxRate ?? undefined}
                  onChange={v => set({ taxRate: v ?? null })}
                  options={TAX_RATES}
                />
              </Col>
            ) : null}
          </>
        ) : row.type === "transfer" ? (
          <Col xs={24} sm={12} md={16}>
            <Select
              showSearch
              allowClear
              style={{ width: "100%" }}
              placeholder={t("Contra account (transit if empty)")}
              value={row.account || undefined}
              onChange={v => set({ account: v || "" })}
              optionFilterProp="label"
              options={(ledgerAccounts || []).map(a => ({
                value: a.number,
                label: `${a.number} ${a.name}`
              }))}
            />
          </Col>
        ) : null}
      </Row>
      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        <Col xs={24} sm={12} md={8}>
          <Typography.Text type="secondary">{t("Amount")}</Typography.Text>
          <MoneyInput value={row.amount} onChange={v => set({ amount: v || 0 })} />
        </Col>
        {row.type === "invoice" || row.type === "beleg" ? (
          <Col xs={12} md={8}>
            <Typography.Text type="secondary">{t("Skonto")}</Typography.Text>
            <MoneyInput value={row.skonto} onChange={v => set({ skonto: v || 0 })} />
          </Col>
        ) : null}
        {row.type === "invoice" ? (
          <Col xs={12} md={8}>
            <Typography.Text type="secondary">{t("Fee")}</Typography.Text>
            <MoneyInput value={row.fee} onChange={v => set({ fee: v || 0 })} />
          </Col>
        ) : null}
        {!isDoc ? (
          <Col xs={24} sm={12} md={16}>
            <Typography.Text type="secondary">{t("Text")}</Typography.Text>
            <Input
              placeholder={t("Text")}
              value={row.label}
              onChange={e => set({ label: e.target.value })}
            />
          </Col>
        ) : null}
      </Row>
    </Card>
  );
};

/** Match / split / ignore / unmatch one bank transaction. */
const MatchDrawer = ({ id, accounts, canWrite, onClose, onChanged }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [remainderError, setRemainderError] = useState(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setRows([]);
    setRemainderError(null);
    try {
      const res = await getTransaction(id);
      setData(res.data);
      if (["matched", "split"].includes(res.data.transaction.matchStatus))
        setRows(res.data.transaction.matches || []);
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (!id || ledgerAccounts.length) return;
    listLedgerAccounts({ active: true })
      .then(res =>
        setLedgerAccounts(
          (res.data.accounts || []).filter(a => !["debitor", "kreditor"].includes(a.type))
        )
      )
      .catch(() => null);
  }, [id]);

  const tx = data?.transaction;
  const incoming = tx ? tx.amount > 0 : true;
  const abs = tx ? Math.abs(tx.amount) : 0;
  const explained = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remainder = abs - explained;
  const editable = canWrite && tx && !["matched", "split"].includes(tx.matchStatus);

  const apply = async () => {
    setBusy(true);
    setRemainderError(null);
    try {
      await matchTransaction(
        id,
        rows.map(r => ({ ...r, taxRate: r.taxRate ?? null }))
      );
      message.success(t("Transaction matched"));
      onChanged();
      load();
    } catch (err) {
      if (err?.response?.data?.code === "REMAINDER") setRemainderError(err.response.data.remainder);
      else message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const applySuggestion = async s => {
    setRows(rowsFromSuggestion(s));
    const sum = rowsFromSuggestion(s).reduce((x, r) => x + (r.amount || 0), 0);
    if (sum === abs) {
      setBusy(true);
      try {
        await matchTransaction(id, rowsFromSuggestion(s));
        message.success(t("Transaction matched"));
        onChanged();
        load();
      } catch (err) {
        message.error(errorText(err));
      } finally {
        setBusy(false);
      }
    }
  };

  const unmatch = () => {
    Modal.confirm({
      title: t("Unmatch this transaction?"),
      content: t("The journal entry is reversed and the payments are removed from the documents."),
      okText: t("Unmatch"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await unmatchTransaction(id, "");
          message.success(t("Transaction unmatched"));
          onChanged();
          load();
        } catch (err) {
          message.error(errorText(err));
        }
      }
    });
  };

  const ignore = () => {
    let note = "";
    Modal.confirm({
      title: t("Ignore this transaction?"),
      content: (
        <div>
          <p>{t("Private or non-business lines are excluded from the books. Add a short note.")}</p>
          <Input placeholder={t("Note")} onChange={e => (note = e.target.value)} />
        </div>
      ),
      okText: t("Ignore"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          await ignoreTransaction(id, note);
          onChanged();
          load();
        } catch (err) {
          message.error(errorText(err));
        }
      }
    });
  };

  const addRow = (partial = {}) =>
    setRows(r => [
      ...r,
      {
        type: "account",
        ref: null,
        account: "",
        category: "",
        taxRate: incoming ? null : 19,
        amount: Math.max(0, remainder),
        skonto: 0,
        fee: 0,
        label: "",
        ...partial
      }
    ]);

  const matched = tx && ["matched", "split"].includes(tx.matchStatus);
  // Actions are pinned in the drawer footer so they stay reachable on phones.
  let footer = null;
  if (!loading && tx && matched && canWrite) {
    footer = (
      <div className="acct-actions" style={{ justifyContent: "flex-end" }}>
        <Button danger onClick={unmatch}>
          {t("Unmatch")}
        </Button>
      </div>
    );
  } else if (!loading && tx && editable) {
    footer = (
      <div
        className="acct-actions"
        style={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography.Text strong type={remainder === 0 ? "success" : "danger"}>
          {t("Remainder")}: <span className="acct-num">{formatCents(remainder)}</span>
        </Typography.Text>
        <div className="acct-actions">
          {tx.matchStatus !== "ignored" ? <Button onClick={ignore}>{t("Ignore")}</Button> : null}
          <Button
            type="primary"
            loading={busy}
            disabled={!rows.length || remainder !== 0}
            onClick={apply}
          >
            {t("Match")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Drawer
      open={Boolean(id)}
      onClose={onClose}
      width={isMobile ? "100%" : 720}
      title={t("Match transaction")}
      rootClassName="acct-module"
      destroyOnClose
      footer={footer}
    >
      {loading || !tx ? (
        <Spin />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label={t("Date")}>{formatDate(tx.bookingDate)}</Descriptions.Item>
            <Descriptions.Item label={t("Amount")}>
              <Amount cents={tx.amount} signed large />
            </Descriptions.Item>
            <Descriptions.Item label={t("Counterpart")} span="filled">
              <span className="acct-wrap">{tx.counterpartName || "–"}</span>
              {tx.counterpartIban ? (
                <Typography.Text type="secondary"> · {tx.counterpartIban}</Typography.Text>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label={t("Purpose")} span="filled">
              <span className="acct-wrap">{tx.purpose || "–"}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t("Account")}>{data.account?.name}</Descriptions.Item>
            <Descriptions.Item label={t("Status")}>
              <Tag color={STATUS_COLORS[tx.matchStatus]}>
                {t(STATUS_LABELS[tx.matchStatus] || tx.matchStatus)}
              </Tag>
              {tx.note ? <Typography.Text type="secondary">{tx.note}</Typography.Text> : null}
            </Descriptions.Item>
          </Descriptions>

          {["matched", "split"].includes(tx.matchStatus) ? (
            <>
              <Typography.Title level={5}>{t("Matched to")}</Typography.Title>
              <List
                size="small"
                bordered
                dataSource={tx.matches || []}
                renderItem={m => (
                  <List.Item>
                    <Space wrap>
                      <Tag>{t(MATCH_TYPE_LABELS[m.type] || m.type)}</Tag>
                      <span className="acct-wrap">{m.label || m.account}</span>
                      <Amount cents={m.amount} strong />
                      {m.skonto ? (
                        <Typography.Text type="secondary">
                          {t("Skonto")} {formatCents(m.skonto)}
                        </Typography.Text>
                      ) : null}
                      {m.fee ? (
                        <Typography.Text type="secondary">
                          {t("Fee")} {formatCents(m.fee)}
                        </Typography.Text>
                      ) : null}
                    </Space>
                  </List.Item>
                )}
              />
              {data.entry ? (
                <Typography.Text type="secondary" className="acct-wrap">
                  {t("Journal entry")} {data.entry.entryNo}/{data.entry.year}:{" "}
                  {data.entry.lines
                    .map(
                      l =>
                        `${l.account} ${l.debit ? "S " + formatCents(l.debit) : "H " + formatCents(l.credit)}`
                    )
                    .join(" · ")}
                </Typography.Text>
              ) : null}
            </>
          ) : (
            <>
              {tx.suggestions?.length ? (
                <>
                  <Typography.Title level={5}>{t("Suggestions")}</Typography.Title>
                  {tx.suggestions.map((s, i) => (
                    <Card
                      key={i}
                      size="small"
                      hoverable={editable}
                      style={{ marginBottom: 8 }}
                      onClick={editable ? () => setRows(rowsFromSuggestion(s)) : undefined}
                      extra={
                        editable ? (
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={busy}
                            onClick={e => {
                              e.stopPropagation();
                              applySuggestion(s);
                            }}
                          >
                            {t("Apply")}
                          </Button>
                        ) : null
                      }
                    >
                      <Space wrap>
                        <Tag
                          color={
                            s.confidence >= 0.95
                              ? "green"
                              : s.confidence >= 0.8
                                ? "gold"
                                : "default"
                          }
                        >
                          {Math.round(s.confidence * 100)} %
                        </Tag>
                        <Tag>{t(MATCH_TYPE_LABELS[s.type] || s.type)}</Tag>
                        <Typography.Text strong className="acct-wrap">
                          {s.label}
                        </Typography.Text>
                        <Amount cents={s.amount} />
                        {s.skonto ? (
                          <Typography.Text type="secondary">
                            {t("Skonto")} {formatCents(s.skonto)}
                          </Typography.Text>
                        ) : null}
                        <Typography.Text type="secondary">({t(s.reason)})</Typography.Text>
                      </Space>
                      {s.items?.length ? (
                        <div style={{ marginTop: 6 }}>
                          {s.items.map((it, j) => (
                            <div key={j}>
                              <Typography.Text type="secondary" className="acct-wrap">
                                {it.label} ·{" "}
                                <span className="acct-num">{formatCents(it.amount)}</span>
                              </Typography.Text>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </Card>
                  ))}
                </>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message={t(
                    "No automatic suggestion. Search a document or pick an account below."
                  )}
                />
              )}
              {editable ? (
                <>
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography.Title level={5}>{t("Assign")}</Typography.Title>
                  <TargetSearch
                    direction={incoming ? "in" : "out"}
                    onPick={target =>
                      addRow({
                        ...target,
                        amount: Math.min(target.amount, Math.max(0, remainder) || abs)
                      })
                    }
                  />
                  <Divider style={{ margin: "8px 0" }} />
                  {rows.map((row, index) => (
                    <MatchRow
                      key={index}
                      row={row}
                      index={index}
                      incoming={incoming}
                      ledgerAccounts={ledgerAccounts}
                      categories={data.categories}
                      onChange={(i, next) =>
                        setRows(list => list.map((r, k) => (k === i ? next : r)))
                      }
                      onRemove={i => setRows(list => list.filter((_, k) => k !== i))}
                    />
                  ))}
                  <div className="acct-actions">
                    <Button icon={<PlusOutlined />} onClick={() => addRow()}>
                      {t("Add row")}
                    </Button>
                    <Button onClick={() => addRow({ type: "private", label: t("Private") })}>
                      {t("Private")}
                    </Button>
                    <Button onClick={() => addRow({ type: "fee", label: t("Bank fee") })}>
                      {t("Bank fee")}
                    </Button>
                    {accounts.length > 1 ? (
                      <Button onClick={() => addRow({ type: "transfer", label: t("Transfer") })}>
                        {t("Transfer")}
                      </Button>
                    ) : null}
                  </div>
                  <Alert
                    type={remainder === 0 && rows.length ? "success" : "warning"}
                    showIcon
                    message={
                      <Space wrap>
                        <span>
                          {t("Assigned")}:{" "}
                          <span className="acct-num">
                            {formatCents(explained)} / {formatCents(abs)}
                          </span>
                        </span>
                        <Typography.Text strong type={remainder === 0 ? "success" : "danger"}>
                          {t("Remainder")}:{" "}
                          <span className="acct-num">{formatCents(remainder)}</span>
                        </Typography.Text>
                        {remainderError !== null ? (
                          <Typography.Text type="danger">
                            ({t("server")}:{" "}
                            <span className="acct-num">{formatCents(remainderError)}</span>)
                          </Typography.Text>
                        ) : null}
                      </Space>
                    }
                  />
                </>
              ) : null}
            </>
          )}
        </Space>
      )}
    </Drawer>
  );
};

export default MatchDrawer;
